// App bootstrap, shell, and client-side routing. Plain DOM, no framework.

import './styles/app.css';
import './styles/print.css';
import { fetchEvents, ApiError } from './lib/api';
import { listOwned } from './lib/ownership';
import { navigate, route, setNotFound, startRouter } from './lib/router';
import { monthEvents, store, visibleEvents, type ViewName } from './lib/state';
import { createSearchBar } from './views/searchBar';
import { createListView } from './views/listView';
import { createCalendarView } from './views/calendarView';
import { createMapView } from './views/mapView';
import { renderCreatePage } from './views/createEventForm';
import { renderEventDetailPage } from './views/eventDetail';
import { renderManagePage } from './views/manageEventPage';
import { renderAdminEditPage, renderAdminPage } from './views/adminPanel';

const app = document.querySelector<HTMLDivElement>('#app')!;

app.innerHTML = `
  <header class="app-header">
    <a class="brand" href="/" data-link>
      <svg class="brand-mark" width="44" height="44" viewBox="0 0 48 48" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="irlEm" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#163b30"/><stop offset="1" stop-color="#0b201a"/></linearGradient>
          <linearGradient id="irlBrass" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f0dca0"/><stop offset="1" stop-color="#c9a44d"/></linearGradient>
          <line id="irlRay" x1="24" y1="19" x2="24" y2="9.4"/>
        </defs>
        <path d="M24 2.5C15 2.5 7.5 9.9 7.5 18.9c0 11.3 16.5 26.6 16.5 26.6s16.5-15.3 16.5-26.6C40.5 9.9 33 2.5 24 2.5Z" fill="url(#irlEm)" stroke="url(#irlBrass)" stroke-width="2.2"/>
        <g stroke="#c9a44d" stroke-width="1" stroke-linecap="round">
          <use href="#irlRay"/><use href="#irlRay" transform="rotate(30 24 19)"/><use href="#irlRay" transform="rotate(60 24 19)"/>
          <use href="#irlRay" transform="rotate(90 24 19)"/><use href="#irlRay" transform="rotate(120 24 19)"/><use href="#irlRay" transform="rotate(150 24 19)"/>
          <use href="#irlRay" transform="rotate(180 24 19)"/><use href="#irlRay" transform="rotate(210 24 19)"/><use href="#irlRay" transform="rotate(240 24 19)"/>
          <use href="#irlRay" transform="rotate(270 24 19)"/><use href="#irlRay" transform="rotate(300 24 19)"/><use href="#irlRay" transform="rotate(330 24 19)"/>
        </g>
        <circle cx="24" cy="19" r="6.7" fill="url(#irlEm)" stroke="url(#irlBrass)" stroke-width="1.4"/>
        <rect x="20.8" y="15.8" width="6.4" height="6.4" rx="0.6" transform="rotate(45 24 19)" fill="url(#irlBrass)"/>
        <rect x="22.5" y="17.5" width="3" height="3" transform="rotate(45 24 19)" fill="#2f9d83"/>
      </svg>
      <div>
        <h1>IRL</h1>
        <p class="tagline">Real events near you. Post yours free.</p>
      </div>
    </a>
    <nav class="header-nav">
      <a class="btn primary" href="/create" data-link>Post an event</a>
    </nav>
  </header>
  <main id="view" class="view-region"></main>
  <footer class="app-footer">
    <p>Aggregated from public sources and community posts. Anyone can post an event for free.</p>
    <p><a href="/admin" data-link class="admin-link">Admin</a></p>
  </footer>
`;

// --- Home page (built once, cached, re-inserted on navigation) ---

let homeEl: HTMLElement | null = null;

async function runSearch(): Promise<void> {
  const state = store.get();
  if (!state.location) return;
  store.set({ status: 'loading', error: undefined });
  try {
    // Categories are intentionally not sent: the client filters by category
    // locally (visibleEvents), so every chip combination shares one cached
    // result set and toggling chips never triggers a network round-trip.
    const res = await fetchEvents({
      lat: state.location.lat,
      lng: state.location.lng,
      keyword: state.keyword || undefined,
      radiusMiles: state.radiusMiles,
    });
    store.set({
      status: 'ready',
      events: res.events,
      meta: res.meta,
      selectedDay: undefined,
      selectedEventId: undefined,
    });
  } catch (err) {
    const msg =
      err instanceof ApiError ? err.message : 'Could not load events. Please try again.';
    store.set({ status: 'error', error: msg });
  }
}

function buildHome(): HTMLElement {
  const home = document.createElement('div');
  home.className = 'home';
  home.innerHTML = `
    <section id="search" class="search-region"></section>
    <section id="your-events" class="your-events" hidden></section>
    <nav class="view-switch" role="tablist" aria-label="Views">
      <button class="vs-btn active" role="tab" data-view="list" aria-selected="true">
        <svg class="vs-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
        <span class="vs-label">Browse</span>
        <span class="vs-count" data-count="list" hidden></span>
      </button>
      <button class="vs-btn" role="tab" data-view="calendar" aria-selected="false">
        <svg class="vs-ico" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16" rx="2"/><path d="M3 9.5h18M8 2.5v4M16 2.5v4"/></svg>
        <span class="vs-label">Calendar</span>
        <span class="vs-count" data-count="calendar" hidden></span>
      </button>
      <button class="vs-btn" role="tab" data-view="map" aria-selected="false">
        <svg class="vs-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s6.5-5.8 6.5-11A6.5 6.5 0 0 0 5.5 10c0 5.2 6.5 11 6.5 11z"/><circle cx="12" cy="10" r="2.3"/></svg>
        <span class="vs-label">Map</span>
        <span class="vs-count" data-count="map" hidden></span>
      </button>
      <span class="vs-glow" aria-hidden="true"></span>
    </nav>
    <div class="views">
      <section id="view-list" class="view" role="tabpanel"></section>
      <section id="view-calendar" class="view" hidden role="tabpanel"></section>
      <section id="view-map" class="view" hidden role="tabpanel"></section>
    </div>
  `;

  createSearchBar(home.querySelector<HTMLElement>('#search')!, { runSearch });
  createListView(home.querySelector<HTMLElement>('#view-list')!);
  createCalendarView(home.querySelector<HTMLElement>('#view-calendar')!, { runSearch });
  createMapView(home.querySelector<HTMLElement>('#view-map')!, { runSearch });

  const btns = Array.from(home.querySelectorAll<HTMLButtonElement>('.vs-btn'));
  const counts: Record<ViewName, HTMLElement> = {
    list: home.querySelector('[data-count="list"]')!,
    calendar: home.querySelector('[data-count="calendar"]')!,
    map: home.querySelector('[data-count="map"]')!,
  };
  const panels: Record<ViewName, HTMLElement> = {
    list: home.querySelector('#view-list')!,
    calendar: home.querySelector('#view-calendar')!,
    map: home.querySelector('#view-map')!,
  };
  const order: ViewName[] = ['list', 'calendar', 'map'];
  const glow = home.querySelector<HTMLElement>('.vs-glow')!;
  for (const btn of btns) {
    btn.addEventListener('click', () => store.setView(btn.dataset.view as ViewName));
  }

  function setCount(el: HTMLElement, n: number): void {
    el.textContent = String(n);
    el.hidden = n === 0;
  }

  store.subscribe((state) => {
    for (const btn of btns) {
      const on = btn.dataset.view === state.activeView;
      btn.setAttribute('aria-selected', String(on));
      btn.classList.toggle('active', on);
    }
    // Slide the active-pill highlight under the selected segment.
    glow.style.transform = `translateX(${order.indexOf(state.activeView) * 100}%)`;
    order.forEach((name) => {
      panels[name].hidden = name !== state.activeView;
    });

    if (state.status === 'ready') {
      const visible = visibleEvents(state);
      setCount(counts.list, visible.length);
      setCount(counts.calendar, monthEvents(state).length);
      setCount(
        counts.map,
        visible.filter((e) => typeof e.lat === 'number' && typeof e.lng === 'number').length
      );
    } else {
      order.forEach((name) => (counts[name].hidden = true));
    }
  });

  return home;
}

function renderYourEvents(home: HTMLElement): void {
  const section = home.querySelector<HTMLElement>('#your-events')!;
  const owned = listOwned();
  if (owned.length === 0) {
    section.hidden = true;
    return;
  }
  section.hidden = false;
  section.innerHTML = `<h2 class="your-events-title">Your events</h2>`;
  const list = document.createElement('div');
  list.className = 'your-events-list';
  for (const o of owned.slice(0, 10)) {
    const row = document.createElement('div');
    row.className = 'your-event-row';
    const name = document.createElement('a');
    name.textContent = o.name;
    name.href = `/e/${o.id}`;
    name.addEventListener('click', (e) => {
      e.preventDefault();
      navigate(`/e/${o.id}`);
    });
    const manage = document.createElement('a');
    manage.className = 'btn small';
    manage.textContent = 'Manage';
    manage.href = new URL(o.manageUrl).pathname;
    manage.addEventListener('click', (e) => {
      e.preventDefault();
      navigate(new URL(o.manageUrl).pathname);
    });
    row.append(name, manage);
    list.appendChild(row);
  }
  section.appendChild(list);
}

function renderHome(root: HTMLElement): void {
  if (!homeEl) homeEl = buildHome();
  renderYourEvents(homeEl);
  root.replaceChildren(homeEl);
}

// --- Routes ---

const view = document.querySelector<HTMLElement>('#view')!;

route('/', renderHome);
route('/create', (root) => renderCreatePage(root));
route('/admin', (root) => renderAdminPage(root));
route('/admin/edit/:id', (root, params) => renderAdminEditPage(root, params));
route('/e/:id/manage/:token', (root, params) => renderManagePage(root, params));
route('/e/:id', (root, params) => renderEventDetailPage(root, params));
setNotFound((root) => {
  root.innerHTML = `<section class="page"><div class="placeholder">Page not found. <a href="/" data-link>Go home</a></div></section>`;
});

startRouter(view);
